import * as React from 'react';
import { CustomInfo } from 'cb-unified';
import { Button, Stack, Tab, Tabs } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  addControlColumn,
  PaginatorAndListSettings,
  PageTitle,
  Search,
  Section,
  selectUser,
  useCacheQuery,
  useCloneEntity,
  getConfigOrThrow,
} from 'gossamer-front-end';
import { EventHostView } from 'cb-model';
import { getFilter, QueryOptions } from 'gossamer-unified';
import { DownloadButton } from 'shared-front-end';
import { UserPaymentsSummary } from '../payments/UserPaymentsSummary';
import { InstitutionPaymentsSummary } from '../payments/InstitutionPaymentsSummary';

export const Hosting = (): React.JSX.Element => {
  const user = useSelector(selectUser);
  const now = React.useRef(new Date().toISOString());
  const adminOfInstitutionId = (user?.customInfo as CustomInfo)?.adminInstitutionId || undefined;
  const hostCondition = React.useRef(getFilter('user_id', 'eq', user?.userId, 'hidden'));

  const [queryOptionsFuture, setQueryOptionsFuture] = React.useState({
    where: {
      and: [
        getFilter('title', 'sw', ''),
        hostCondition.current,
        getFilter('visibility_limit', 'ne', 'cancelled', 'hidden'),
        getFilter('type', 'ne', 'on-demand', 'hidden'),
        getFilter('start_at', 'gt', now.current, 'hidden'),
      ],
    },
    order: [['start_at', 'ASC']],
  } as QueryOptions);

  const [queryOptionsPast, setQueryOptionsPast] = React.useState({
    where: {
      and: [
        getFilter('title', 'sw', ''),
        hostCondition.current,
        getFilter('visibility_limit', 'ne', 'cancelled', 'hidden'),
        getFilter('type', 'ne', 'on-demand', 'hidden'),
        getFilter('start_at', 'lt', now.current, 'hidden'),
      ],
    },
    order: [['start_at', 'DESC']],
  } as QueryOptions);

  const [queryOptionsCancelled, setQueryOptionsCancelled] = React.useState({
    where: {
      and: [
        getFilter('title', 'sw', ''),
        hostCondition.current,
        getFilter('visibility_limit', 'eq', 'cancelled', 'hidden'),
      ],
    },
    order: [['start_at', 'DESC']],
  } as QueryOptions);

  const LocalPastEventHostView = useCloneEntity(EventHostView, (local) => {
    addControlColumn(
      local,
      'download_certificate',
      999,
      (row) => {
        const url = [
          getConfigOrThrow('gossamer-be-origin'),
          'gossamer/main/certificate-host',
          row.event_id,
          row.user_id,
        ].join('/');
        console.log('composing url:', row.event_id, row.user_id, url);
        return url;
      },
      (props) => <DownloadButton url={props.value}>certificate</DownloadButton>,
    );
  });

  hostCondition.current.value = user?.userId;

  const gCacheQueryItemFuture = useCacheQuery(EventHostView, queryOptionsFuture, !user?.userId);
  const gCacheQueryItemPast = useCacheQuery(LocalPastEventHostView, queryOptionsPast, !user?.userId);
  const gCacheQueryItemCancelled = useCacheQuery(EventHostView, queryOptionsCancelled, !user?.userId);

  return (
    <>
      <PageTitle title="Event Hosting" />
      <Stack direction="horizontal" gap={3} className="mb-3">
        <Button variant="primary" href="/admin/event/create">
          Create an Event
        </Button>
        {!adminOfInstitutionId && (
          <Button variant="secondary" href="/admin/payments/user-summary">
            Payments Summary
          </Button>
        )}
      </Stack>

      <Tabs variant="underline">
        <Tab eventKey="upcoming" title="Upcoming">
          <Section subtitle="Future Events of which I am the Main Host or Co-Host">
            <PaginatorAndListSettings
              entity={EventHostView}
              gCacheQuery={gCacheQueryItemFuture}
              queryOptions={queryOptionsFuture}
              render={Search}
              setQueryOptions={setQueryOptionsFuture}
            />
          </Section>
        </Tab>
        <Tab eventKey="past" title="Past">
          <Section subtitle="Past Events of which I was the Main Host or Co-Host">
            <PaginatorAndListSettings
              entity={LocalPastEventHostView}
              gCacheQuery={gCacheQueryItemPast}
              queryOptions={queryOptionsPast}
              render={Search}
              setQueryOptions={setQueryOptionsPast}
            />
          </Section>
        </Tab>
        <Tab eventKey="cancelled" title="Cancelled">
          <Section subtitle="Cancelled Events of which I was the Main Host or Co-Host">
            <PaginatorAndListSettings
              entity={EventHostView}
              gCacheQuery={gCacheQueryItemCancelled}
              queryOptions={queryOptionsCancelled}
              render={Search}
              setQueryOptions={setQueryOptionsCancelled}
            />
          </Section>
        </Tab>
        <Tab eventKey="payments" title="Payments Summary">
          {!adminOfInstitutionId && <UserPaymentsSummary />}
          {!!adminOfInstitutionId && <InstitutionPaymentsSummary />}
        </Tab>
      </Tabs>
    </>
  );
};
